import MuiButton, {
  ButtonProps as MuiButtonProps,
  ButtonPropsColorOverrides,
  ButtonPropsSizeOverrides,
  ButtonPropsVariantOverrides,
} from '@mui/material/Button';
import { OverridableStringUnion } from '@mui/types';
import { forwardRef, ReactElement } from 'react';
import { styled } from '@mui/material/styles';
import { CircularProgress as MuiCircularProgress, CircularProgressProps } from '@mui/material';

// CircularProgress specification - sizing specific for button: from Design library -- font size of button variants.
const CPSIZE = {
  SMALL: 12,
  MEDIUM: 14,
  LARGE: 18,
};

interface CPAddedMarginProps {
  mx?: string;
  my?: string;
}

interface StyledCPOwnerState {
  ownerState: CPAddedMarginProps;
}

const StyledCircularProgress = styled(MuiCircularProgress)<StyledCPOwnerState>(({ ownerState }) => ({
  ...(ownerState.mx && {
    marginLeft: ownerState.mx,
    marginRight: ownerState.mx,
  }),
  ...(ownerState.my && {
    marginTop: ownerState.my,
    marginBottom: ownerState.my,
  }),
}));

declare module '@mui/material/Button' {
  export interface ButtonPropsColorOverrides {
    internal: true;
  }

  export interface ButtonPropsSizeOverrides {
    uniform: true;
  }

  export interface ButtonPropsVariantOverrides {
    opaque: true;
  }
}

export * from '@mui/material/Button';

export interface ButtonProps extends MuiButtonProps {
  color?: OverridableStringUnion<
    'primary' | 'secondary' | 'internal' | 'success' | 'error' | 'inherit',
    ButtonPropsColorOverrides
  >;
  /**
   * Same as Material UI variant, except 'text' variant is semi-transparent.
   */
  variant?: OverridableStringUnion<'text' | 'contained' | 'outlined' | 'opaque', ButtonPropsVariantOverrides>;
  size?: OverridableStringUnion<'small' | 'medium' | 'large' | 'uniform', ButtonPropsSizeOverrides>;
  component?: 'span' | 'p' | 'div' | 'button' | ReactElement;
  loading?: boolean;
  loadingText?: string | ReactElement;
}

/**
 * Default button has outlined variant. We also have extra colors.
 */
const Button = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const { color = 'primary', variant = 'outlined', size = 'medium', startIcon = null, ...other } = props;

  if (props.loading) {
    const cpSize: CircularProgressProps['size'] =
      size === 'small' ? CPSIZE.SMALL : size === 'large' ? CPSIZE.LARGE : CPSIZE.MEDIUM;

    const cpOwnerState: CPAddedMarginProps = {
      mx: size === 'small' || size === 'medium' ? '3px' : size === 'large' ? '2px' : undefined,
      my: size === 'uniform' ? '5px' : undefined,
    };

    return (
      <MuiButton
        ref={ref}
        color={color}
        variant={variant}
        size={size}
        {...other}
        disabled
        startIcon={<StyledCircularProgress color="inherit" size={cpSize} ownerState={cpOwnerState} />}
        endIcon={undefined}
      >
        {props.loadingText ?? props.children}
      </MuiButton>
    );
  }
  return <MuiButton ref={ref} color={color} variant={variant} size={size} startIcon={startIcon} {...other} />;
});

Button.displayName = 'Button';

export default Button;
